import React, { useEffect, useState } from "react";
import { Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import styled from "styled-components";
import BackButton from "../../../components/custom/BackButton";
import ActionIcon from "../../../components/custom/ActionIcon";
import { createdUpdatedInfo } from "../../../utils";
import { getErrorMessage } from "../../dev/Inventory/errorMessage";
import EntitySelect from "../../../components/Select";
import { SelectOption } from "../../../globals/interfaces";
import { AssignPayload, EntityOption, EntityType, InventoryItem } from "./types";
import {
    AssignAvail,
    AssignContext,
    Block,
    ErrorText,
    FieldLabel,
    FlexContainer,
    FooterButtonsContainer,
    FooterContentContainer,
    HeaderContainer,
    Input,
    RequiredMark,
    Select,
} from "./styles";
import useUser from "../../../customHooks/useUser";
import useExportData from "./useExportData";
import { Button } from "reactstrap";
import apiClient from "../../../apiClient";
import useUnit from "../../../customHooks/useUnit";

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

interface AssignInventoryModalProps {
    modal: boolean;
    setModal: (open: boolean) => void;
    item: InventoryItem | null;
    getItems: () => Promise<void>;
    refreshSerials: () => void;
}

export default function AddQuantityModal({ modal, setModal, item, getItems, refreshSerials }: AssignInventoryModalProps) {
    const unit = useUnit()

    const [quantity, setQuantity] = useState("1");
    const [errors, setErrors] = useState<Record<string, string>>({});
    const available = item?.availableQuantity ?? 0;

    useEffect(() => {
        setQuantity("1");
    }, [modal]);

    const toggle = () => setModal(!modal);

    async function addQty(itemId: number, body: { quantity: number; unit: string; }) {
        await apiClient.post(`/inventory/items/${itemId}/units`, body);
        // then re-fetch the list so availableQuantity updates
        await getItems(); // GET /inventory/items?unit=<currentUnit>
        refreshSerials();
    }

    const save = async () => {
        const e: Record<string, string> = {};
        const q = Number(quantity);
        if (quantity === "" || Number.isNaN(q) || q < 1) e.quantity = "Assign at least one.";
        setErrors(e);
        if (Object.keys(e).length > 0) return;

        try {
            await addQty(Number(item.id), {
                quantity: Number(quantity),
                unit
            });
            toggle();
        } catch (err) {
            // A 409 here (not enough stock at the source unit) is a normal outcome,
            // not a bug — surface it inline on the quantity field rather than a toast.
            setErrors({ quantity: getErrorMessage(err, "Couldn't assign that.") });
        }
    }

    if (!item) return null;

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={"d-flex"}>
                <HeaderContainer>
                    <FlexContainer>
                        <BackButton onClick={toggle} />
                        <h4 className={"pl-2"}>Add Quantity for {item.name}</h4>
                    </FlexContainer>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <AssignContext>
                    <span>{item.name}</span>
                    <AssignAvail>{available} on hand</AssignAvail>
                </AssignContext>

                <Block mt={16}>
                    <FieldLabel>
                        Quantity <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <Input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        mono
                        error={!!errors.quantity}
                    />
                    {errors.quantity && <ErrorText>{errors.quantity}</ErrorText>}
                </Block>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <FooterButtonsContainer>
                        <ActionIcon type={"save"} onClick={save} />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}
